// 門口即時影像頁的門鈴列（docs/panel 05-ui-ux V-14，TP6.6 / TP6.11）：
//   - 別端已接聽 →「○○ 已接聽」（仍可看門口）
//   - 從門鈴卡 / 轉到手機推播進來（?ring_id=&handoff_id=）→ 一鍵［接聽］（不直接接聽）；畫面出來後才能按
//   - ［🚪 開門］：有 door_open 才顯示 → 確認「開前門？」→ 結果照 SD §7.2a
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { signaling } from '../../lib/ws';
import { deviceName } from '../devices/deviceModel';
import { currentUserId } from '../devices/panels';
import { useDeviceList } from '../devices/useDeviceList';
import { answeredText, answerFrame, answerOutcome, doorbellAnswered, type AnsweredBy } from './doorbellAnswered';
import { canOpenDoor, DoorOpener, type DoorPhase, type DoorSignaling } from './doorOpen';
import './doorbell.css';

export function DoorbellLiveBar({ deviceId, ringId, handoffId, streaming, ready }: {
  deviceId: string;
  ringId?: string;
  handoffId?: string;
  /** 門口畫面出來了（signaling 要先 start_viewing 才承認接聽） */
  streaming: boolean;
  /** signaling 開著 */
  ready: boolean;
}) {
  const { t } = useTranslation();
  const { devices } = useDeviceList();
  const dev = useMemo(() => devices?.find((d) => d.device_id === deviceId) ?? null, [devices, deviceId]);
  const names = useMemo(() => new Map((devices ?? []).map((d) => [d.device_id, deviceName(d)])), [devices]);
  useSyncExternalStore(doorbellAnswered.subscribe, doorbellAnswered.version);
  const other = doorbellAnswered.get(deviceId);

  const [answer, setAnswer] = useState<'idle' | 'busy' | 'ok' | 'error'>('idle');
  const [answeredBy, setAnsweredBy] = useState<AnsweredBy | null>(null);
  const [door, setDoor] = useState<DoorPhase>('idle');
  const opener = useRef<DoorOpener | null>(null);

  useEffect(() => {
    opener.current = new DoorOpener(signaling as unknown as DoorSignaling, deviceId, setDoor);
    return () => opener.current?.stop();
  }, [deviceId]);

  const doAnswer = async () => {
    if (!ringId) return;
    setAnswer('busy');
    try {
      const o = answerOutcome(await signaling.requestFrame(answerFrame(deviceId, ringId, handoffId)));
      if (o.status === 'ok') setAnswer('ok');
      else if (o.status === 'already_answered') {
        setAnsweredBy(o.by);
        setAnswer('idle');
      } else setAnswer('error');
    } catch {
      setAnswer('error');
    }
  };

  const showDoor = canOpenDoor(dev);
  const by = answeredBy ?? (other && (!ringId || !other.ringId || other.ringId === ringId) ? other.by : null);
  if (!ringId && !by && !showDoor) return null;
  const me = currentUserId();

  return (
    <div className="card db-live-bar" data-testid="doorbell-live-bar">
      {by && answer !== 'ok' && (
        <p className="db-answered" data-testid="doorbell-answered" role="status">{answeredText(t, by, names, me)}</p>
      )}
      {ringId && !by && answer !== 'ok' && (
        <div className="dv-pairing-actions">
          <button className="btn-primary" onClick={() => void doAnswer()} disabled={!streaming || answer === 'busy'} data-testid="doorbell-answer">
            {t('doorbell.answerNow')}
          </button>
          {!streaming && <span className="text-secondary">{t('doorbell.answerWait')}</span>}
        </div>
      )}
      {answer === 'ok' && <p className="text-secondary" role="status">{t('doorbell.answeredByYou')}</p>}
      {answer === 'error' && <p className="error-text">{t('doorbell.answerFailed')}</p>}

      {showDoor && (
        <div className="db-door" data-testid="door-open">
          {door === 'idle' && (
            <button className="btn-primary" onClick={() => setDoor('confirm')} disabled={!ready} data-testid="door-open-btn">
              🚪 {t('doorOpen.button')}
            </button>
          )}
          {door === 'confirm' && (
            <div role="dialog" aria-label={t('doorOpen.confirm')}>
              <p>{t('doorOpen.confirm')}</p>
              <div className="dv-pairing-actions">
                <button className="btn-primary" onClick={() => opener.current?.send(false)} data-testid="door-open-yes">{t('doorOpen.yes')}</button>
                <button className="btn-ghost" onClick={() => setDoor('idle')}>{t('common.cancel')}</button>
              </div>
            </div>
          )}
          {door === 'sending' && <p role="status">{t('doorOpen.sending')}</p>}
          {door === 'ok' && (
            <p className="db-door-ok" role="status" data-testid="door-result">
              {t('doorOpen.ok')} <button className="btn-ghost" onClick={() => setDoor('idle')}>{t('doorOpen.back')}</button>
            </p>
          )}
          {door === 'fail' && (
            <div role="status" data-testid="door-result">
              <p className="error-text">{t('doorOpen.fail')}</p>
              <div className="dv-pairing-actions">
                <button className="btn-primary" onClick={() => opener.current?.send(false)}>{t('doorOpen.retry')}</button>
                <button className="btn-ghost" onClick={() => setDoor('idle')}>{t('doorOpen.back')}</button>
              </div>
            </div>
          )}
          {door === 'unknown' && (
            <div role="status" data-testid="door-result">
              <p className="error-text">{t('doorOpen.unknown')}</p>
              <div className="dv-pairing-actions">
                <button className="btn-primary" onClick={() => opener.current?.send(true)}>{t('doorOpen.resend')}</button>
                <button className="btn-ghost" onClick={() => setDoor('idle')}>{t('doorOpen.back')}</button>
              </div>
            </div>
          )}
          {door === 'error' && (
            <p className="error-text" role="status" data-testid="door-result">
              {t('doorOpen.error')} <button className="btn-ghost" onClick={() => setDoor('idle')}>{t('doorOpen.back')}</button>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
